import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';

import { useTherapeuticClassExportService, TherapeuticClassExportParams } from '@/api/use-therapeutic-class-export';

interface UseTherapeuticClassExportOptions {
  exportParams?: TherapeuticClassExportParams;
}

export const useTherapeuticClassExport = ({
  exportParams,
}: UseTherapeuticClassExportOptions) => {
  const [isExporting, setIsExporting] = useState(false);
  const { exportToCSV } = useTherapeuticClassExportService();

  const handleExport = useCallback(async () => {
    if (!exportParams) {
      toast.error('Export parameters are required for CSV export');
      return;
    }

    setIsExporting(true);
    
    // Show loading toast
    const loadingToastId = toast.loading('Preparing CSV export...');
    
    try {
      const fileDownload = await exportToCSV(exportParams);
      
      // Create unique filename with timestamp to avoid browser cache issues
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const baseFilename = fileDownload.filename.replace(/\.[^/.]+$/, ''); // Remove extension
      const extension = fileDownload.filename.match(/\.[^/.]+$/)?.[0] || '.csv';
      const uniqueFilename = `${baseFilename}_${timestamp}${extension}`;
      const url = URL.createObjectURL(fileDownload.contents);
      
      try {
        let downloadSuccessful = false;
        
        // Try modern download approach first (supported in modern browsers)
        if (typeof window !== 'undefined' && 'showSaveFilePicker' in window) {
          try {
            const fileHandle = await (window as any).showSaveFilePicker({
              suggestedName: uniqueFilename,
              types: [{
                description: 'CSV files',
                accept: { 'text/csv': ['.csv'] }
              }]
            });
            const writable = await fileHandle.createWritable();
            await writable.write(fileDownload.contents);
            await writable.close();
            downloadSuccessful = true;
          } catch (fsError) {
            // User cancelled or API not available - silently fallback
            // This is expected behavior, not an error
          }
        }
        
        // Fallback to traditional link download if modern approach failed
        if (!downloadSuccessful) {
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', uniqueFilename);
          link.style.visibility = 'hidden';
          link.style.position = 'absolute';
          link.style.left = '-9999px';
          
          // Ensure the link is added to DOM before clicking
          document.body.appendChild(link);
          
          // Trigger download
          link.click();
          
          // Clean up after a short delay
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
          }, 100);
        }
        
        // Always clean up the blob URL
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
        
        // Success notification
        toast.success('CSV file download started!', { id: loadingToastId });
        
      } catch (downloadError) {
        // Clean up URL in case of error
        URL.revokeObjectURL(url);
        toast.error('Failed to download CSV file. Please try again.', { id: loadingToastId });
        throw downloadError;
      }
      
    } catch (error) {
      toast.error('Failed to prepare CSV export. Please try again.', { id: loadingToastId });
      throw error; // Re-throw so calling code can handle it
    } finally {
      setIsExporting(false);
    }
  }, [exportParams, exportToCSV]);

  return {
    handleExport,
    canExport: !!exportParams,
    isExporting,
  };
};
